import { useEffect, useState } from 'react';
import { clsx } from 'clsx';

import { TaskField } from '@atom/types/task';
import {
  CustomFieldView,
  getTaskFieldWidth,
} from '@atom/utilities/taskUtilities';

import classes from './taskFields.module.css';

type TaskEnumSingleFieldListProps = {
  field: TaskField;
  onChange: (fieldId: string, value: any) => void;
  isDisabled?: boolean;
  error?: boolean;
  helperText?: string;
  invalidFieldIds?: Set<string>;
  onClick?: () => void;
  customFieldView: CustomFieldView;
  fieldsLoading: Set<string>;
  dataCy?: string;
};

const TaskEnumSingleFieldList = ({
  field,
  onChange,
  isDisabled,
  error,
  helperText,
  invalidFieldIds = new Set([]),
  onClick,
  customFieldView,
  fieldsLoading,
  dataCy,
}: TaskEnumSingleFieldListProps) => {
  const [value, setValue] = useState<string>(field.value);
  const disableField = fieldsLoading.has(field.id) || isDisabled;
  const inputError = error || invalidFieldIds.has(field.id);
  const baseDataCy = dataCy ?? `taskField-${field.title}-EnumSingleList`;

  const handleChange = (option: string) => {
    const newValue = option === value ? '' : option;
    setValue(newValue);
    onChange(field.id, newValue);
  };

  useEffect(() => {
    setValue(field.value);
  }, [field?.value]);

  const title =
    field.required && customFieldView !== CustomFieldView.FILTER
      ? `* ${field.title}`
      : field.title;

  const containerWidth = {
    maxWidth: getTaskFieldWidth(customFieldView),
  };

  return (
    <>
      <div
        className={classes.fieldContainer}
        onClick={onClick}
        style={containerWidth}
        data-cy={baseDataCy}
      >
        <div
          className={clsx(
            classes.fieldTitle,
            inputError && classes.fieldTitleError,
          )}
        >
          {title}
        </div>
        <div className={classes.listContainer}>
          {field.enumeration.map(option => (
            <div key={option} className={classes.listItem}>
              <div className={classes.listInputContainer}>
                <input
                  className={classes.listInput}
                  type="radio"
                  onClick={() => handleChange(option)}
                  checked={value === option}
                  disabled={disableField}
                  data-cy={`${baseDataCy}-option-${option}`}
                />
              </div>
              <div onClick={() => !disableField && handleChange(option)}>
                {option}
              </div>
            </div>
          ))}
        </div>
        {inputError && helperText && (
          <div className={classes.listHelperText}>{helperText}</div>
        )}
      </div>
    </>
  );
};

export default TaskEnumSingleFieldList;
